import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import {
    Container,
    Section,
    PageHero,
    AmberButton,
} from "@/components/Primitives";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
    {
        q: "What does \u201cthe aegis\u201d mean?",
        a: "An aegis is protection, backing, and authority — the force that holds a standard that cannot be compromised. Here it's the methodology itself: the rules that don't bend, the discipline that doesn't tune on sessions it watched, the honest acknowledgment that paper results are a ceiling not a forecast. The aegis is what makes the line hold.",
    },
    {
        q: "Is this a get-rich bot?",
        a: "No. It's a disciplined trading engine. Day trading carries real risk of loss, and we don't promise returns. What we promise is a transparent, rule-bounded process.",
    },
    {
        q: "Why an LLM instead of a pure algorithm?",
        a: "Because the decision benefits from pattern recognition over messy, structured context — the part a fixed rule tree handles poorly. The analysis stays deterministic; only the judgment is delegated to the model.",
    },
    {
        q: "Can the AI just make things up?",
        a: "It's constrained: it can't invent price levels (they're copied from computed structure), direction is determined in code, and its decision is written down with reasons. The brief it reads is facts, not verdicts.",
    },
    {
        q: "Is it live or paper?",
        a: "Paper-trading evaluation today. We say so everywhere, and paper results are an upper bound on live performance. We'll announce clearly if and when that changes.",
    },
    {
        q: "What does it trade?",
        a: "Low-float gap-and-go stocks at the open, and index micro futures (MES, MNQ) on the opening and overnight range.",
    },
    {
        q: "Will it work in every market condition?",
        a: "No system does. The Aegis Line targets specific, high-energy windows and passes when conditions don't fit. \u201cPass\u201d is a valid, frequent answer — and it's logged just like any other decision.",
    },
    {
        q: "Do you take a cut of my profits?",
        a: "No. Flat subscription. We're not incentivised to over-trade your account.",
    },
    {
        q: "Why should I trust a system that's still in paper trading?",
        a: "You probably shouldn't — yet. That's the honest answer. Paper-trading evaluation exists precisely so that we don't ask you to trust it before it's earned that. Watch the methodology, read the decision logs, and make that call yourself when the time comes.",
    },
];

export default function Faq() {
    return (
        <>
            <Seo
                title="FAQ"
                description="Straight answers about The Aegis Line: paper vs live, why an LLM, whether the AI can make things up, what it trades, and how pricing works."
            />
            <PageHero
                eyebrow="Objection-handling"
                title="Straight answers."
                sub="If a question deserves a candid answer, you'll find it here — including the uncomfortable ones."
            />

            <Section>
                <Container className="max-w-3xl">
                    <Reveal>
                        <Accordion type="single" collapsible className="w-full">
                            {FAQS.map((f, i) => (
                                <AccordionItem
                                    key={i}
                                    value={`item-${i}`}
                                    data-testid={`faq-item-${i}`}
                                    className="border-haze"
                                >
                                    <AccordionTrigger className="py-6 text-left text-base font-semibold text-text-hi hover:no-underline sm:text-lg">
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="pb-6 text-sm leading-relaxed text-text-lo sm:text-base">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>

                    <Reveal>
                        <div className="mt-12 rounded-lg border border-haze bg-ink-soft p-7 text-center">
                            <p className="text-base text-text-hi">
                                Still weighing it up?
                            </p>
                            <p className="mt-2 text-sm text-text-lo">
                                Read how we keep ourselves honest.
                            </p>
                            <div className="mt-6 flex justify-center">
                                <AmberButton
                                    to="/methodology"
                                    testId="faq-methodology-cta"
                                >
                                    Read the methodology <ArrowRight size={16} />
                                </AmberButton>
                            </div>
                        </div>
                    </Reveal>
                </Container>
            </Section>
        </>
    );
}
